/**
 * AI Estimate Review Copilot (DREAM 4.1) — the office-facing "Review this
 * estimate" panel. Shows plain-English findings from the server's deterministic
 * engine (plus an optional AI summary), each with Apply (the one safe title
 * fix) / Dismiss. Read-only otherwise — Apply routes through the audited,
 * draft-only server endpoint.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CheckCircle2, Loader2, Sparkles, Wand2 } from "lucide-react";
import { toast } from "sonner";

interface Finding {
    code: string;
    severity: "high" | "medium" | "low";
    what: string;
    why: string;
    apply?: { field: string; value: string };
    lineId?: string;
}
interface Review {
    estimateId: string;
    status: string;
    editable: boolean;
    findings: Finding[];
    aiEnabled: boolean;
    aiNarrative: string | null;
}

const sevTone: Record<string, string> = {
    high: "border-red-300 bg-red-50 text-red-800",
    medium: "border-amber-300 bg-amber-50 text-amber-900",
    low: "border-slate-300 bg-slate-50 text-slate-700",
};

const findingKey = (f: Finding) => f.code + (f.lineId ?? "");

export function EstimateReviewPanel({ estimateId }: { estimateId: string }) {
    const qc = useQueryClient();
    const [dismissed, setDismissed] = useState<Set<string>>(() => new Set());

    const q = useQuery({
        queryKey: ["estimate-review", estimateId],
        queryFn: () => api.get<Review>(`/api/estimates/${estimateId}/review`),
    });

    const apply = useMutation({
        mutationFn: (code: string) => api.post(`/api/estimates/${estimateId}/review/apply`, { code }),
        onSuccess: async () => {
            toast.success("Applied — estimate updated");
            await qc.invalidateQueries({ queryKey: ["estimate", estimateId] });
            await qc.invalidateQueries({ queryKey: ["estimate-review", estimateId] });
        },
        onError: (e: any) => toast.error(`Couldn't apply: ${e?.body?.error ?? e?.message ?? "unknown"}`),
    });

    const review = q.data;
    const findings = (review?.findings ?? []).filter((f) => !dismissed.has(findingKey(f)));

    return (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-teal-600" />
                    Review this estimate
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                {q.isLoading && (
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" /> Checking the estimate…
                    </p>
                )}

                {review && findings.length === 0 && (
                    <p className="text-sm text-emerald-700 flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4" /> Nothing to flag — this estimate looks clean.
                    </p>
                )}

                {review?.aiNarrative && findings.length > 0 && (
                    <p className="text-sm text-foreground/80 bg-teal-50 dark:bg-teal-950/40 border border-teal-100 dark:border-teal-800/70 rounded-md px-3 py-2">
                        {review.aiNarrative}
                    </p>
                )}

                {findings.map((f) => (
                    <div
                        key={findingKey(f)}
                        className={`rounded-md border px-3 py-2 ${sevTone[f.severity] ?? sevTone.low}`}
                    >
                        <div className="flex items-start gap-2">
                            <AlertTriangle className="h-4 w-4 mt-0.5 flex-none" />
                            <div className="flex-1">
                                <p className="text-sm font-semibold">{f.what}</p>
                                <p className="text-xs opacity-90 mt-0.5">{f.why}</p>
                                <div className="flex items-center gap-2 mt-2">
                                    {f.apply && review?.editable && (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="h-7"
                                            disabled={apply.isPending}
                                            onClick={() => apply.mutate(f.code)}
                                        >
                                            {apply.isPending ? (
                                                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                                            ) : (
                                                <Wand2 className="h-3.5 w-3.5 mr-1" />
                                            )}
                                            Apply fix
                                        </Button>
                                    )}
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-7"
                                        onClick={() => setDismissed((s) => new Set(s).add(findingKey(f)))}
                                    >
                                        Dismiss
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}

                {review && !review.editable && findings.length > 0 && (
                    <p className="text-xs text-muted-foreground">
                        This estimate isn't a draft — fixes can't be auto-applied.
                    </p>
                )}
            </CardContent>
        </Card>
    );
}
